
/**
 * Sound Notification System
 * Plays notification sounds for various app events
 */

export type SoundType = 'download' | 'success' | 'error' | 'warning' | 'info' | 'notification';

export interface SoundConfig {
  enabled: boolean;
  volume: number;
  sounds: Record<SoundType, string>;
}

class SoundNotificationSystem {
  private static instance: SoundNotificationSystem;
  private config: SoundConfig;
  private audioCache: Map<string, HTMLAudioElement> = new Map();
  private isInitialized: boolean = false;

  private constructor() {
    this.config = this.loadConfig();
    this.initializeAudio();
  }

  public static getInstance(): SoundNotificationSystem {
    if (!SoundNotificationSystem.instance) {
      SoundNotificationSystem.instance = new SoundNotificationSystem();
    }
    return SoundNotificationSystem.instance;
  }

  private loadConfig(): SoundConfig {
    const saved = localStorage.getItem('sound_notification_config');
    const defaultConfig: SoundConfig = {
      enabled: true,
      volume: 0.5,
      sounds: {
        download: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmcgBybY3/LFeyMFI3rM8dN3IgYdcsH25qNQEAg+Zs8y',
        success: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559MEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmcgBybY3/LFeyMFI3rM8dN3IgYdcsH25qNQEAg+Zs8y',
        error: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559MEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmcgBybY3/LFeyMFI3rM8dN3IgYdcsH25qNQEAg+Zs8y',
        warning: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559MEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmcgBybY3/LFeyMFI3rM8dN3IgYdcsH25qNQEAg+Zs8y',
        info: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559MEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmcgBybY3/LFeyMFI3rM8dN3IgYdcsH25qNQEAg+Zs8y',
        notification: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmcgBybY3/LFeyMFI3rM8dN3IgYdcsH25qNQEAg+Zs8y'
      }
    };

    if (saved) {
      try {
        return { ...defaultConfig, ...JSON.parse(saved) };
      } catch {
        return defaultConfig;
      }
    }
    return defaultConfig;
  }

  private async initializeAudio(): Promise<void> {
    if (this.isInitialized) return;
    
    console.log('🔊 Initializing Sound Notification System with embedded audio');
    
    // Preload sounds immediately
    this.preloadSounds();
    this.isInitialized = true;
  }

  private preloadSounds(): void {
    console.log('🔊 Preloading notification sounds...');
    
    // Get unique sound paths
    const uniquePaths = [...new Set(Object.values(this.config.sounds))];
    
    uniquePaths.forEach(soundPath => {
      if (!this.audioCache.has(soundPath)) {
        const audio = new Audio();
        audio.volume = this.config.volume;
        audio.preload = 'auto';
        
        // Handle successful loading
        audio.addEventListener('canplaythrough', () => {
          console.log(`✅ Sound loaded: ${soundPath.substring(0, 50)}...`);
        });
        
        // Handle loading errors
        audio.addEventListener('error', (e) => {
          console.warn(`❌ Failed to load sound: ${soundPath.substring(0, 50)}...`, e);
        });
        
        audio.src = soundPath;
        this.audioCache.set(soundPath, audio);
      }
    });
  }

  public async playSound(type: SoundType): Promise<void> {
    if (!this.config.enabled) {
      console.log(`🔕 Sound disabled, skipping ${type} sound`);
      return;
    }

    console.log(`🔊 Playing ${type} sound...`);

    try {
      const soundPath = this.config.sounds[type];
      if (!soundPath) {
        console.warn(`❌ No sound configured for type: ${type}`);
        this.playFallbackBeep();
        return;
      }

      let audio = this.audioCache.get(soundPath);
      if (!audio) {
        console.log(`🔄 Creating new audio element for: ${type}`);
        audio = new Audio(soundPath);
        audio.volume = this.config.volume;
        this.audioCache.set(soundPath, audio);
      }

      // Reset audio to beginning
      audio.currentTime = 0;
      audio.volume = this.config.volume;
      
      // Create promise to handle play attempt
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        await playPromise;
        console.log(`✅ Successfully played ${type} sound`);
      }
    } catch (error) {
      console.warn(`❌ Failed to play ${type} sound:`, error);
      
      // Try to create a simple beep as fallback
      this.playFallbackBeep();
    }
  }

  private playFallbackBeep(): void {
    try {
      // Create a simple beep using Web Audio API as fallback
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
      
      console.log('🔔 Played fallback beep');
    } catch (error) {
      console.warn('❌ Failed to play fallback beep:', error);
    }
  }

  public updateConfig(newConfig: Partial<SoundConfig>): void {
    this.config = { ...this.config, ...newConfig };
    localStorage.setItem('sound_notification_config', JSON.stringify(this.config));
    
    // Update volume for cached audio elements
    this.audioCache.forEach(audio => {
      audio.volume = this.config.volume;
    });
    
    console.log('🔧 Sound config updated:', this.config);
  }

  public getConfig(): SoundConfig {
    return { ...this.config };
  }

  public isEnabled(): boolean {
    return this.config.enabled;
  }

  public setEnabled(enabled: boolean): void {
    this.updateConfig({ enabled });
    console.log(`🔊 Sound notifications ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Test method to play and verify sound
  public testSound(type: SoundType): void {
    console.log(`🧪 Testing ${type} sound...`);
    this.playSound(type);
  }

  // Debug method to check audio cache status
  public getAudioCacheStatus(): Record<string, string> {
    const status: Record<string, string> = {};
    this.audioCache.forEach((audio, path) => {
      status[path.substring(0, 50) + '...'] = audio.readyState >= 2 ? 'loaded' : 'loading';
    });
    return status;
  }
}

export const soundSystem = SoundNotificationSystem.getInstance();
